/**
 * Asset Class Defaults page (Sprint C15-C WP E-3).
 *
 * Aaim platform-admin CRUD surface for the canonical asset class taxonomy
 * (AssetClassDefinition). Tenant-specific policy overrides (haircut,
 * eligibility, max concentration) are managed per-tenant on the Tenant
 * Detail page and are NOT surfaced here.
 *
 * Backend: GET/POST /api/admin/asset-classes,
 *          GET/PUT/DELETE /api/admin/asset-classes/:assetClassCode
 * Auth:    platform-admin role required (FI tenant operators 403).
 *
 * Vocabulary. platform-admin.openapi.yaml AssetClass, AssetClassCreate and
 * AssetClassUpdate type groupCode, uccClassification, valuationComplexity and
 * liquidityProfile by reference to the regulatory-readiness enums
 * (AssetClassGroupCode, UccClassification, ValuationComplexity,
 * LiquidityProfile), which every stored row matches (op-32). The labels and
 * form options below are keyed by those generated enums, so tsc fails here
 * when the spec adds a member, and the form can only send a member. The form
 * once offered LOW/MEDIUM/HIGH and DAILY/T+1/T+3/WEEKLY/MONTHLY/ILLIQUID, so
 * editing a VERY_HIGH or HIGHLY_LIQUID row opened a select showing a value it
 * did not hold (op-g2-15). The group code was free text; it is now a select
 * over AssetClassGroupCode.
 */

import { useState, useMemo, useCallback } from 'react';
import { ChevronDown, ChevronRight, PlusIcon, Pencil, Trash2 } from 'lucide-react';
import {
  Alert,
  FilterBar,
  Button,
  BodyText,
  DataGrid,
  PageHeader,
  Skeleton,
  Dialog,
} from '@aaimplatform/ui';
import type {
  ColumnDef,
  FilterDefinition,
  ActiveFilter,
} from '@aaimplatform/ui';
import type { components as RegulatoryReadinessComponents } from '@aaimplatform/institutional-types/regulatory-readiness';
import {
  useAssetClasses,
  useCreateAssetClass,
  useUpdateAssetClass,
  useDeleteAssetClass,
} from '../../hooks/useAssetClasses';
import type {
  AssetClass,
  AssetClassCreate,
  AssetClassUpdate,
  RegulatoryPerfectionMethod,
} from '../../api/client';
import { UNRECOGNIZED_LABEL, enumLabel } from '../../utils/status-labels';

type UccClassification = RegulatoryReadinessComponents['schemas']['UccClassification'];
type ValuationComplexity = RegulatoryReadinessComponents['schemas']['ValuationComplexity'];
type LiquidityProfile = RegulatoryReadinessComponents['schemas']['LiquidityProfile'];
type AssetClassGroupCode = RegulatoryReadinessComponents['schemas']['AssetClassGroupCode'];

/**
 * The create and edit form: AssetClassCreate with the four enum fields empty
 * until the operator picks a member.
 */
type AssetClassFormData = Omit<
  AssetClassCreate,
  'groupCode' | 'uccClassification' | 'valuationComplexity' | 'liquidityProfile'
> & {
  groupCode: AssetClassCreate['groupCode'] | '';
  uccClassification: AssetClassCreate['uccClassification'] | '';
  valuationComplexity: AssetClassCreate['valuationComplexity'] | '';
  liquidityProfile: AssetClassCreate['liquidityProfile'] | '';
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PERFECTION_METHODS: { value: RegulatoryPerfectionMethod; label: string }[] = [
  { value: 'CONTROL_AGREEMENT_8106', label: 'Control Agreement (UCC 8-106)' },
  { value: 'CONTROL_CER_12105', label: 'Control via CER (UCC 12-105)' },
  { value: 'UCC1_FILING_9310', label: 'UCC-1 Filing (9-310)' },
  { value: 'POSSESSION_9313', label: 'Possession (9-313)' },
  { value: 'AUTOMATIC_9309', label: 'Automatic (9-309)' },
  { value: 'INTERMEDIARY_OPT_IN_8102', label: 'Intermediary Opt-In (8-102)' },
];

/** UCC Article 9 collateral types (regulatory-readiness UccClassification). */
export const UCC_CLASSIFICATION_LABEL: Record<UccClassification, string> = {
  INVESTMENT_PROPERTY: 'Investment property',
  CONTROLLABLE_ELECTRONIC_RECORD: 'Controllable electronic record',
  GENERAL_INTANGIBLE: 'General intangible',
  DEPOSIT_ACCOUNT: 'Deposit account',
  INSTRUMENT: 'Instrument',
};

export const VALUATION_COMPLEXITY_LABEL: Record<ValuationComplexity, string> = {
  LOW: 'Low',
  MODERATE: 'Moderate',
  HIGH: 'High',
  VERY_HIGH: 'Very high',
};

export const LIQUIDITY_PROFILE_LABEL: Record<LiquidityProfile, string> = {
  HIGHLY_LIQUID: 'Highly liquid',
  MODERATELY_LIQUID: 'Moderately liquid',
  ILLIQUID: 'Illiquid',
};

/**
 * AssetClassGroupCode members, in the page's group order. A group code is an
 * identifier and its option prints the code itself; groupName carries the
 * group's display name.
 */
export const GROUP_CODE_OPTION_LABEL: Record<AssetClassGroupCode, string> = {
  ALTERNATIVE: 'ALTERNATIVE',
  DIGITAL_TIER1: 'DIGITAL_TIER1',
  EQUITY_COMP: 'EQUITY_COMP',
  TRADITIONAL: 'TRADITIONAL',
};

/**
 * A select value as the enum member it names, or '' when the map does not
 * carry it. The options come from the map, so a change event always names a
 * member; the check keeps the form state typed by the generated enum.
 */
export function memberOrEmpty<K extends string>(map: Record<K, string>, value: string): K | '' {
  return Object.prototype.hasOwnProperty.call(map, value) ? (value as K) : '';
}

/**
 * Select options for a label map. A stored value outside the map is kept as
 * one more option, marked unrecognized, so a controlled select shows the value
 * the row holds instead of silently displaying the first option.
 */
export function selectOptions<K extends string>(
  map: Record<K, string>,
  current: string,
): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = (Object.keys(map) as K[]).map((value) => ({
    value,
    label: map[value],
  }));
  if (current && !Object.prototype.hasOwnProperty.call(map, current)) {
    options.push({ value: current, label: `${UNRECOGNIZED_LABEL} (${current})` });
  }
  return options;
}

const EMPTY_CREATE: AssetClassFormData = {
  assetClassCode: '',
  name: '',
  groupCode: '',
  groupName: '',
  uccClassification: '',
  primaryPerfection: 'UCC1_FILING_9310',
  article12Dependent: false,
  intermediaryRequired: false,
  valuationComplexity: '',
  liquidityProfile: '',
  regulatoryOverlays: [],
};

/** A vocabulary value as its label; outside the enum it reads "Unrecognized" with the code in its title. */
function VocabularyText<K extends string>({ map, value }: { map: Record<K, string>; value: string }) {
  const label = enumLabel(map, value);
  return (
    <span
      className="text-sm font-sans text-secondary leading-normal"
      title={label === UNRECOGNIZED_LABEL ? value : undefined}
    >
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------

function useAssetClassColumns(
  onEdit: (row: AssetClass) => void,
  onDelete: (row: AssetClass) => void,
): ColumnDef<AssetClass, unknown>[] {
  return useMemo<ColumnDef<AssetClass, unknown>[]>(
    () => [
      {
        accessorKey: 'assetClassCode',
        header: 'Code',
        // The code is the immutable business key: an identifier, set in
        // monospace, and not in the accent red reserved for calls to action.
        cell: ({ getValue }) => (
          <span className="text-sm font-mono font-medium text-primary leading-normal">
            {getValue() as string}
          </span>
        ),
        size: 180,
      },
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ getValue }) => (
          <span className="text-sm font-sans font-medium text-primary leading-normal">
            {getValue() as string}
          </span>
        ),
        size: 240,
      },
      {
        // Headers name the field in full, as the edit form does: "Valuation"
        // over "Very high" read as a price level (op-g2-10).
        accessorKey: 'uccClassification',
        header: 'UCC classification',
        cell: ({ getValue }) => (
          <VocabularyText map={UCC_CLASSIFICATION_LABEL} value={getValue() as string} />
        ),
        size: 210,
      },
      {
        accessorKey: 'valuationComplexity',
        header: 'Valuation complexity',
        cell: ({ getValue }) => (
          <VocabularyText map={VALUATION_COMPLEXITY_LABEL} value={getValue() as string} />
        ),
        size: 120,
      },
      {
        accessorKey: 'liquidityProfile',
        header: 'Liquidity profile',
        cell: ({ getValue }) => (
          <VocabularyText map={LIQUIDITY_PROFILE_LABEL} value={getValue() as string} />
        ),
        size: 150,
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-2 justify-end">
            <button
              type="button"
              className="p-1.5 text-tertiary hover:text-primary transition-colors"
              aria-label={`Edit ${row.original.assetClassCode}`}
              onClick={(event) => {
                event.stopPropagation();
                onEdit(row.original);
              }}
            >
              <Pencil className="w-4 h-4" aria-hidden />
            </button>
            <button
              type="button"
              className="p-1.5 text-tertiary hover:text-error transition-colors"
              aria-label={`Delete ${row.original.assetClassCode}`}
              onClick={(event) => {
                event.stopPropagation();
                onDelete(row.original);
              }}
            >
              <Trash2 className="w-4 h-4" aria-hidden />
            </button>
          </div>
        ),
        size: 90,
      },
    ],
    [onEdit, onDelete],
  );
}

// ---------------------------------------------------------------------------
// AssetClassDefaultsPage
// ---------------------------------------------------------------------------

export function AssetClassDefaultsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [editTarget, setEditTarget] = useState<AssetClass | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AssetClass | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data: response, isLoading, error } = useAssetClasses();
  const deleteAssetClass = useDeleteAssetClass();

  const assetClasses: AssetClass[] = useMemo(
    () => response?.data ?? [],
    [response],
  );

  // -------------------------------------------------------------------------
  // Filter definitions -- groups and UCC classifications are derived from data
  // -------------------------------------------------------------------------

  const filterDefinitions: FilterDefinition[] = useMemo(() => {
    const groups = new Map<string, string>();
    const uccClasses = new Set<string>();
    for (const item of assetClasses) {
      if (item.groupCode) groups.set(item.groupCode, item.groupName || item.groupCode);
      if (item.uccClassification) uccClasses.add(item.uccClassification);
    }
    return [
      {
        id: 'groupCode',
        label: 'Group',
        type: 'select',
        options: Array.from(groups.entries())
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([value, label]) => ({ value, label })),
      },
      {
        id: 'uccClassification',
        label: 'UCC Classification',
        type: 'select',
        options: Array.from(uccClasses)
          .sort()
          .map((u) => ({ value: u, label: enumLabel(UCC_CLASSIFICATION_LABEL, u) })),
      },
    ];
  }, [assetClasses]);

  // -------------------------------------------------------------------------
  // Filtered rows (search + active filters)
  // -------------------------------------------------------------------------

  const filteredRows = useMemo(() => {
    let result = [...assetClasses];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (item) =>
          item.assetClassCode.toLowerCase().includes(q) ||
          item.name.toLowerCase().includes(q),
      );
    }
    for (const filter of activeFilters) {
      if (filter.filterId === 'groupCode') {
        result = result.filter((item) => item.groupCode === filter.value);
      } else if (filter.filterId === 'uccClassification') {
        result = result.filter(
          (item) => item.uccClassification === filter.value,
        );
      }
    }
    return result;
  }, [assetClasses, searchQuery, activeFilters]);

  // -------------------------------------------------------------------------
  // Hierarchy: group filtered rows by groupCode (F-PA-ASSET-TAXONOMY-001).
  // Stable group order for canonical taxonomy presentation. Unknown groups
  // surface alphabetically after the canonical four.
  // -------------------------------------------------------------------------

  const CANONICAL_GROUP_ORDER = ['ALTERNATIVE', 'DIGITAL_TIER1', 'EQUITY_COMP', 'TRADITIONAL'];

  const groupedRows = useMemo(() => {
    const groups = new Map<string, { groupCode: string; groupName: string; items: AssetClass[] }>();
    for (const item of filteredRows) {
      const code = item.groupCode || 'UNGROUPED';
      const existing = groups.get(code);
      if (existing) {
        existing.items.push(item);
      } else {
        groups.set(code, {
          groupCode: code,
          groupName: item.groupName || code,
          items: [item],
        });
      }
    }
    const entries = Array.from(groups.values());
    entries.sort((a, b) => {
      const ai = CANONICAL_GROUP_ORDER.indexOf(a.groupCode);
      const bi = CANONICAL_GROUP_ORDER.indexOf(b.groupCode);
      if (ai === -1 && bi === -1) return a.groupCode.localeCompare(b.groupCode);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
    return entries;
  }, [filteredRows]);

  // Collapsed-group state. Default expanded; persists per-session.
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const toggleGroup = useCallback((groupCode: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupCode)) {
        next.delete(groupCode);
      } else {
        next.add(groupCode);
      }
      return next;
    });
  }, []);

  // -------------------------------------------------------------------------
  // Delete handler
  // -------------------------------------------------------------------------

  const handleEdit = useCallback((row: AssetClass) => {
    setActionError(null);
    setEditTarget(row);
  }, []);
  const handleDelete = useCallback((row: AssetClass) => {
    setActionError(null);
    setDeleteTarget(row);
  }, []);
  const columns = useAssetClassColumns(handleEdit, handleDelete);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setActionError(null);
    try {
      await deleteAssetClass.mutateAsync(deleteTarget.assetClassCode);
      setDeleteTarget(null);
    } catch (err) {
      setActionError(
        err instanceof Error
          ? err.message
          : 'Failed to delete asset class.',
      );
    }
  }, [deleteAssetClass, deleteTarget]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div>
      <PageHeader
        description="Aaim canonical asset taxonomy. Tenant-specific policy overrides (haircut, eligibility, max concentration) are managed per-tenant on the Tenant Detail page."
        actions={
          <Button
            variant="primary"
            icon={PlusIcon}
            iconPosition="left"
            onClick={() => {
              setActionError(null);
              setShowCreateDialog(true);
            }}
          >
            Add Asset Class
          </Button>
        }
      />

      {/* Error state */}
      {error && (
        <Alert type="error" title="Asset classes could not be read" className="mb-6">
          {error.message}
        </Alert>
      )}

      {actionError && (
        <Alert type="error" className="mb-6">{actionError}</Alert>
      )}

      {/* Filter bar */}
      <FilterBar
        filters={filterDefinitions}
        activeFilters={activeFilters}
        onFiltersChange={setActiveFilters}
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search by code or name..."
        className="rounded-t-sm"
      />

      {/* Hierarchical group view (F-PA-ASSET-TAXONOMY-001).
          Canonical Aaim taxonomy is grouped by groupCode (ALTERNATIVE,
          DIGITAL_TIER1, EQUITY_COMP, TRADITIONAL). Each group is
          collapsible and renders its rows in the kit DataGrid. The panels
          use the surface token: a literal bg-white left the theme-colored
          row text nearly invisible in dark theme (op-g2-2). */}
      {isLoading ? (
        <div className="bg-surface rounded-sm shadow-sm p-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full mb-2" />
          ))}
        </div>
      ) : groupedRows.length === 0 ? (
        <div className="bg-surface rounded-b-sm shadow-sm p-8 text-center">
          <BodyText variant="ui" className="text-tertiary">
            {error ? 'Asset classes could not be read.' : 'No asset classes match the current filter.'}
          </BodyText>
        </div>
      ) : (
        <div className="bg-surface rounded-b-sm shadow-sm divide-y divide-recessed">
          {groupedRows.map((group) => {
            const collapsed = collapsedGroups.has(group.groupCode);
            return (
              <section key={group.groupCode} aria-label={group.groupName}>
                <button
                  type="button"
                  onClick={() => toggleGroup(group.groupCode)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-canvas hover:bg-recessed/40 transition-colors text-left"
                  aria-expanded={!collapsed}
                  aria-controls={`asset-group-${group.groupCode}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {collapsed ? (
                      <ChevronRight className="w-4 h-4 text-tertiary flex-shrink-0" aria-hidden />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-tertiary flex-shrink-0" aria-hidden />
                    )}
                    <span className="text-sm font-sans font-medium text-primary">
                      {group.groupName}
                    </span>
                    {group.groupName !== group.groupCode && (
                      <span className="text-xs font-mono text-tertiary">{group.groupCode}</span>
                    )}
                  </div>
                  <span className="text-xs font-mono text-tertiary tabular-nums flex-shrink-0">
                    {group.items.length} {group.items.length === 1 ? 'class' : 'classes'}
                  </span>
                </button>
                {!collapsed && (
                  <div id={`asset-group-${group.groupCode}`}>
                    {/* No per-group pagination: the group header prints the
                        count, and four footers served 13 rows (op-g2-9). */}
                    <DataGrid<AssetClass>
                      data={group.items}
                      columns={columns}
                      enablePagination={false}
                      hoverable
                      getRowId={(row) => row.assetClassCode}
                      emptyMessage="No asset classes in this group"
                      className="w-full"
                    />
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      {/* Create dialog */}
      <CreateAssetClassDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
      />

      {/* Edit dialog */}
      {editTarget && (
        <EditAssetClassDialog
          assetClass={editTarget}
          open={Boolean(editTarget)}
          onOpenChange={(open) => {
            if (!open) setEditTarget(null);
          }}
        />
      )}

      {/* Delete confirmation */}
      <Dialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title="Delete Asset Class"
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              className="bg-error hover:bg-error/90"
              onClick={handleDeleteConfirm}
              disabled={deleteAssetClass.isPending}
            >
              {deleteAssetClass.isPending ? 'Deleting...' : 'Delete Asset Class'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <BodyText variant="ui">
            This will permanently delete the asset class:
          </BodyText>
          {deleteTarget && (
            <div className="bg-canvas border border-recessed rounded-sm p-3">
              <div className="text-sm font-mono font-medium text-primary">
                {deleteTarget.assetClassCode}
              </div>
              <div className="text-sm font-sans text-secondary">
                {deleteTarget.name}
              </div>
            </div>
          )}
          <BodyText variant="ui" className="text-sm text-secondary">
            Deletion will fail if any perfection-path or readiness-signal rows
            still reference this asset class.
          </BodyText>
        </div>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create dialog
// ---------------------------------------------------------------------------

function CreateAssetClassDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const create = useCreateAssetClass();
  const [formData, setFormData] = useState<AssetClassFormData>(EMPTY_CREATE);
  const [overlaysText, setOverlaysText] = useState('');
  const [formError, setFormError] = useState('');

  const reset = () => {
    setFormData(EMPTY_CREATE);
    setOverlaysText('');
    setFormError('');
  };

  const handleSubmit = async () => {
    if (!formData.assetClassCode.trim()) {
      setFormError('Asset class code is required.');
      return;
    }
    if (!formData.name.trim()) {
      setFormError('Name is required.');
      return;
    }
    const { groupCode, uccClassification, valuationComplexity, liquidityProfile } = formData;
    if (!groupCode || !formData.groupName.trim()) {
      setFormError('Group code and group name are required.');
      return;
    }
    if (!uccClassification) {
      setFormError('UCC classification is required.');
      return;
    }
    if (!valuationComplexity || !liquidityProfile) {
      setFormError('Valuation complexity and liquidity profile are required.');
      return;
    }

    const overlays = overlaysText
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    try {
      await create.mutateAsync({
        ...formData,
        groupCode,
        uccClassification,
        valuationComplexity,
        liquidityProfile,
        regulatoryOverlays: overlays,
      });
      reset();
      onOpenChange(false);
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : 'Failed to create asset class.',
      );
    }
  };

  const handleCancel = () => {
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Add Asset Class"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={create.isPending}
          >
            {create.isPending ? 'Creating...' : 'Create Asset Class'}
          </Button>
        </>
      }
    >
      <AssetClassForm
        mode="create"
        formData={formData}
        setFormData={setFormData}
        overlaysText={overlaysText}
        setOverlaysText={setOverlaysText}
        formError={formError}
      />
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Edit dialog
// ---------------------------------------------------------------------------

function EditAssetClassDialog({
  assetClass,
  open,
  onOpenChange,
}: {
  assetClass: AssetClass;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const update = useUpdateAssetClass();
  // Edit uses the create shape minus the assetClassCode (which is read-only).
  const [formData, setFormData] = useState<AssetClassFormData>({
    assetClassCode: assetClass.assetClassCode,
    name: assetClass.name,
    groupCode: assetClass.groupCode,
    groupName: assetClass.groupName,
    uccClassification: assetClass.uccClassification,
    primaryPerfection: assetClass.primaryPerfection,
    article12Dependent: assetClass.article12Dependent,
    intermediaryRequired: assetClass.intermediaryRequired,
    valuationComplexity: assetClass.valuationComplexity,
    liquidityProfile: assetClass.liquidityProfile,
    regulatoryOverlays: assetClass.regulatoryOverlays,
  });
  const [overlaysText, setOverlaysText] = useState(
    assetClass.regulatoryOverlays.join(', '),
  );
  const [formError, setFormError] = useState('');

  const handleSubmit = async () => {
    const { groupCode, uccClassification, valuationComplexity, liquidityProfile } = formData;
    if (!formData.name.trim() || !groupCode) {
      setFormError('Name and group code are required.');
      return;
    }
    if (!uccClassification || !valuationComplexity || !liquidityProfile) {
      setFormError('UCC classification, valuation complexity and liquidity profile are required.');
      return;
    }

    const overlays = overlaysText
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    const body: AssetClassUpdate = {
      name: formData.name,
      groupCode,
      groupName: formData.groupName,
      uccClassification,
      primaryPerfection: formData.primaryPerfection,
      article12Dependent: formData.article12Dependent,
      intermediaryRequired: formData.intermediaryRequired,
      valuationComplexity,
      liquidityProfile,
      regulatoryOverlays: overlays,
    };

    try {
      await update.mutateAsync({
        assetClassCode: assetClass.assetClassCode,
        body,
      });
      onOpenChange(false);
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : 'Failed to update asset class.',
      );
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Edit ${assetClass.assetClassCode}`}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={update.isPending}
          >
            {update.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </>
      }
    >
      <AssetClassForm
        mode="edit"
        formData={formData}
        setFormData={setFormData}
        overlaysText={overlaysText}
        setOverlaysText={setOverlaysText}
        formError={formError}
      />
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Shared form
// ---------------------------------------------------------------------------

function AssetClassForm({
  mode,
  formData,
  setFormData,
  overlaysText,
  setOverlaysText,
  formError,
}: {
  mode: 'create' | 'edit';
  formData: AssetClassFormData;
  setFormData: (next: AssetClassFormData) => void;
  overlaysText: string;
  setOverlaysText: (next: string) => void;
  formError: string;
}) {
  const set = <K extends keyof AssetClassFormData>(
    key: K,
    value: AssetClassFormData[K],
  ) => {
    setFormData({ ...formData, [key]: value });
  };

  // Field ids are per mode so the create and edit dialogs never share one.
  const fieldId = (name: string) => `asset-class-${mode}-${name}`;

  const labelClass =
    'block text-label-xs font-sans font-semibold uppercase text-tertiary leading-normal mb-1.5';
  const inputClass =
    'w-full px-3 py-2.5 border border-recessed rounded-sm bg-transparent text-sm font-sans text-primary placeholder:text-tertiary focus:outline-none focus:border-accent';
  const monoInputClass = inputClass.replace('font-sans', 'font-mono');

  return (
    <div className="space-y-4">
      {formError && <Alert type="error">{formError}</Alert>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor={fieldId('code')} className={labelClass}>Asset Class Code</label>
          <input
            id={fieldId('code')}
            type="text"
            value={formData.assetClassCode}
            onChange={(e) => set('assetClassCode', e.target.value)}
            placeholder="e.g. PUB_SEC"
            className={monoInputClass}
            disabled={mode === 'edit'}
            aria-readonly={mode === 'edit'}
            aria-describedby={mode === 'edit' ? fieldId('code-note') : undefined}
          />
          {mode === 'edit' && (
            <div id={fieldId('code-note')} className="text-xs font-sans text-tertiary mt-1">
              Code is the immutable business key and cannot be changed.
            </div>
          )}
        </div>
        <div>
          <label htmlFor={fieldId('name')} className={labelClass}>Display Name</label>
          <input
            id={fieldId('name')}
            type="text"
            value={formData.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="e.g. Publicly Traded Securities"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor={fieldId('group-code')} className={labelClass}>Group Code</label>
          <select
            id={fieldId('group-code')}
            value={formData.groupCode}
            onChange={(e) => set('groupCode', memberOrEmpty(GROUP_CODE_OPTION_LABEL, e.target.value))}
            className={monoInputClass}
          >
            <option value="" disabled>
              Select a group code
            </option>
            {selectOptions(GROUP_CODE_OPTION_LABEL, formData.groupCode).map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor={fieldId('group-name')} className={labelClass}>Group Name</label>
          <input
            id={fieldId('group-name')}
            type="text"
            value={formData.groupName}
            onChange={(e) => set('groupName', e.target.value)}
            placeholder="e.g. Traditional Securities"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor={fieldId('ucc')} className={labelClass}>UCC Classification</label>
          <select
            id={fieldId('ucc')}
            value={formData.uccClassification}
            onChange={(e) =>
              set('uccClassification', memberOrEmpty(UCC_CLASSIFICATION_LABEL, e.target.value))
            }
            className={inputClass}
          >
            <option value="" disabled>
              Select a classification
            </option>
            {selectOptions(UCC_CLASSIFICATION_LABEL, formData.uccClassification).map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor={fieldId('perfection')} className={labelClass}>Primary Perfection Method</label>
          <select
            id={fieldId('perfection')}
            value={formData.primaryPerfection}
            onChange={(e) =>
              set(
                'primaryPerfection',
                e.target.value as RegulatoryPerfectionMethod,
              )
            }
            className={inputClass}
          >
            {PERFECTION_METHODS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor={fieldId('valuation')} className={labelClass}>Valuation Complexity</label>
          <select
            id={fieldId('valuation')}
            value={formData.valuationComplexity}
            onChange={(e) =>
              set('valuationComplexity', memberOrEmpty(VALUATION_COMPLEXITY_LABEL, e.target.value))
            }
            className={inputClass}
          >
            <option value="" disabled>
              Select a complexity
            </option>
            {selectOptions(VALUATION_COMPLEXITY_LABEL, formData.valuationComplexity).map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor={fieldId('liquidity')} className={labelClass}>Liquidity Profile</label>
          <select
            id={fieldId('liquidity')}
            value={formData.liquidityProfile}
            onChange={(e) =>
              set('liquidityProfile', memberOrEmpty(LIQUIDITY_PROFILE_LABEL, e.target.value))
            }
            className={inputClass}
          >
            <option value="" disabled>
              Select a profile
            </option>
            {selectOptions(LIQUIDITY_PROFILE_LABEL, formData.liquidityProfile).map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2 pt-6">
          <input
            id={fieldId('article12')}
            type="checkbox"
            checked={formData.article12Dependent}
            onChange={(e) => set('article12Dependent', e.target.checked)}
            className="h-4 w-4 border-recessed rounded text-accent focus:ring-accent"
          />
          <label
            htmlFor={fieldId('article12')}
            className="text-sm font-sans text-primary"
          >
            Article 12 Dependent
          </label>
        </div>
        <div className="flex items-center gap-2 pt-6">
          <input
            id={fieldId('intermediary')}
            type="checkbox"
            checked={formData.intermediaryRequired}
            onChange={(e) => set('intermediaryRequired', e.target.checked)}
            className="h-4 w-4 border-recessed rounded text-accent focus:ring-accent"
          />
          <label
            htmlFor={fieldId('intermediary')}
            className="text-sm font-sans text-primary"
          >
            Intermediary Required
          </label>
        </div>
      </div>

      <div>
        <label htmlFor={fieldId('overlays')} className={labelClass}>Regulatory Overlays</label>
        <input
          id={fieldId('overlays')}
          type="text"
          value={overlaysText}
          onChange={(e) => setOverlaysText(e.target.value)}
          placeholder="Comma-separated, e.g. REG_T, REG_U, BSA"
          className={inputClass}
          aria-describedby={fieldId('overlays-note')}
        />
        <div id={fieldId('overlays-note')} className="text-xs font-sans text-tertiary mt-1">
          Comma-separated list of regulatory tags. Leave empty for none.
        </div>
      </div>
    </div>
  );
}
